import { PlanPredictCurvesRequestPayload, PlanPredictCurvesResponsePayload } from 'api/curves';

import { CurveType, EMISSION_OPTIONS, EmissionCurvesType, P_Curve } from 'types/Curve';
import { PlanModelType } from 'types/PlanPredictModel';

import { CHART_P_CURVES, MONTH_NUMBER_DATA_KEY } from '../constants';
import { PLAN_CURVE_CHART_KEY, PLAN_CURVE_DATA_KEY, PlanProdChartItem, PlanProdDataItem } from './types';

export const getPlannedWellDataKey = (payload: PlanPredictCurvesRequestPayload) => JSON.stringify(payload);

export const generateDataKeyByPlanDataItem = (dataItem: PlanPredictCurvesResponsePayload): PLAN_CURVE_DATA_KEY => {
  return dataItem.P_Curve === P_Curve.P50
    ? `${dataItem.Task}_${PlanModelType.PLANNED}`
    : `${dataItem.Task}_${PlanModelType.PLANNED}_${dataItem.P_Curve}`;
};

const getDataItemDataByKey = (dataByType: PlanPredictCurvesResponsePayload, key: string) => ({
  [generateDataKeyByPlanDataItem(dataByType)]: dataByType[key],
});

export const normalizeChartData = (
  rawData: PlanPredictCurvesResponsePayload[],
  wellIdNumbers: number[],
  predictDataObjects: number,
): { [key: string]: PlanProdDataItem[] } => {
  const object: any = {};
  wellIdNumbers.forEach((wellId, i) => {
    object[wellId] = rawData.slice(i * predictDataObjects, (i + 1) * predictDataObjects).reduce((acc, dataByType) => {
      const chartDataForType = Object.keys(dataByType).reduce((acc, dataByTypeKey) => {
        const monthNumber = Number(dataByTypeKey);

        return !Number.isNaN(monthNumber)
          ? [
              ...acc,
              {
                ...getDataItemDataByKey(dataByType, dataByTypeKey),
                [MONTH_NUMBER_DATA_KEY]: monthNumber,
              },
            ]
          : acc;
      }, [] as Partial<PlanProdDataItem>[]);

      return chartDataForType.map((chartValue, index) => ({
        ...acc[index],
        ...chartValue,
      }));
    }, [] as PlanProdDataItem[]);
  });
  Object.keys(object).forEach((key) => {
    object[key] = object[key].sort(
      (itemA: PlanProdDataItem, itemB: PlanProdDataItem) => itemA.monthNumber - itemB.monthNumber,
    );
  });

  return object;
};

export const generatePlanChartItemByDataKey = (wellDataItem: PlanProdDataItem, wellId: number): PlanProdChartItem => {
  return Object.keys(wellDataItem).reduce((acc, wellDataKey) => {
    return wellDataKey === MONTH_NUMBER_DATA_KEY
      ? { ...acc, [MONTH_NUMBER_DATA_KEY]: wellDataItem[MONTH_NUMBER_DATA_KEY] }
      : {
          ...acc,
          [`${wellDataKey}_${wellId}`]: wellDataItem[wellDataKey as PLAN_CURVE_DATA_KEY],
        };
  }, {} as PlanProdChartItem);
};

export const generatePlanChartKeys = (
  curveType: CurveType,
  modelKeyType: PlanModelType,
  wellId: number | null,
  withGeneral = true,
  withErrors = false,
): PLAN_CURVE_CHART_KEY[] => {
  return [
    ...(withGeneral
      ? [`${curveType}_${modelKeyType}${wellId === null ? '' : `_${wellId}`}` as PLAN_CURVE_CHART_KEY]
      : []),
    ...(withErrors
      ? CHART_P_CURVES.map(
          (pCurve) =>
            `${curveType}_${modelKeyType}_${pCurve}${wellId === null ? '' : `_${wellId}`}` as PLAN_CURVE_CHART_KEY,
        )
      : []),
  ];
};

export const getProdTypeModelByDataKey = (dataKey: PLAN_CURVE_CHART_KEY) => {
  return dataKey.split('_')[0] as CurveType;
};

export const getPlanPredictModelByDataKey = (dataKey: PLAN_CURVE_CHART_KEY) => {
  return dataKey.split('_')[1] as PlanModelType;
};

export const getPCurveByDataKey = (dataKey: PLAN_CURVE_CHART_KEY) => {
  const pCurveValue = dataKey.split('_')[2];

  return Number.isNaN(Number(pCurveValue)) ? (pCurveValue as P_Curve) : undefined;
};

export const getWellIdByDataKey = (dataKey: PLAN_CURVE_CHART_KEY) => {
  const pCurveValue = getPCurveByDataKey(dataKey);

  return Number(dataKey.split('_')[pCurveValue ? 3 : 2]);
};

export const getStrokeWidth = (activeWellId: number | null, dataKey: PLAN_CURVE_CHART_KEY) => {
  return activeWellId === getWellIdByDataKey(dataKey) ? 2 : 1;
};

export const getCurveAttributesByDataKey = (dataKey: PLAN_CURVE_CHART_KEY) => {
  let result;
  const [prodType, modelType, pCurve, wellId] = dataKey.split('_');

  if (wellId === undefined) {
    const wellIdValue = Number(pCurve);

    result = Number.isNaN(wellIdValue)
      ? [prodType, modelType, pCurve, undefined]
      : [prodType, modelType, undefined, wellIdValue];
  } else {
    result = [prodType, modelType, pCurve, Number(wellId)];
  }

  return result as [CurveType, PlanModelType, P_Curve | undefined, number | undefined];
};

export const getValidatedValue = (min: number | null, max: number | null, value: number) => {
  if (min === null && max === null) return value;
  if (min === null) return max && value < max ? value : null;
  if (max === null) return value > min ? value : null;

  return value > min && value < max ? value : null;
};

export const getFilteredEmissionData = (emmisionChartData: Record<string, number>[], wellId: number) => {
  return emmisionChartData.map((item: Record<string, number>) => {
    const itemObject: { [key: string]: number } = {};
    Object.keys(item).forEach((key) => {
      const splitKeyValues = key.split('_');
      const wellIdFromKey = splitKeyValues.length === 1 ? wellId : Number(splitKeyValues[splitKeyValues.length - 1]);
      if (wellIdFromKey === wellId) {
        itemObject[key] = item[key];
      }
    });

    return itemObject;
  });
};

export const getEmissionRawData = (
  emmisionChartData: Record<string, number>[],
  wellId: number,
): PlanPredictCurvesResponsePayload<EmissionCurvesType>[] => {
  return EMISSION_OPTIONS.flatMap((curveType) => {
    const PCurve10: { [key: string | number]: string | number } = { P_Curve: P_Curve.P10, Task: curveType, wellId };
    const PCurve30: { [key: string | number]: string | number } = { P_Curve: P_Curve.P30, Task: curveType, wellId };
    const PCurve50: { [key: string | number]: string | number } = { P_Curve: P_Curve.P50, Task: curveType, wellId };
    const PCurve70: { [key: string | number]: string | number } = { P_Curve: P_Curve.P70, Task: curveType, wellId };
    const PCurve90: { [key: string | number]: string | number } = { P_Curve: P_Curve.P90, Task: curveType, wellId };

    emmisionChartData.forEach((item: Record<string, number>) => {
      Object.keys(item).forEach((key) => {
        const splitKeyValues = key.split('_');
        const wellIdFromKey = Number(splitKeyValues[splitKeyValues.length - 1]);

        if (wellIdFromKey === wellId) {
          if (key.includes(P_Curve.P10) && key.includes(curveType)) {
            PCurve10[item[MONTH_NUMBER_DATA_KEY]] = item[key];
          } else if (key.includes(P_Curve.P30) && key.includes(curveType)) {
            PCurve30[item[MONTH_NUMBER_DATA_KEY]] = item[key];
          } else if (splitKeyValues.length === 3 && key.includes(curveType)) {
            PCurve50[item[MONTH_NUMBER_DATA_KEY]] = item[key];
          } else if (key.includes(P_Curve.P70) && key.includes(curveType)) {
            PCurve70[item[MONTH_NUMBER_DATA_KEY]] = item[key];
          } else if (key.includes(P_Curve.P90) && key.includes(curveType)) {
            PCurve90[item[MONTH_NUMBER_DATA_KEY]] = item[key];
          }
        }
      });
    });

    const result: PlanPredictCurvesResponsePayload<EmissionCurvesType>[] = [
      PCurve10,
      PCurve30,
      PCurve50,
      PCurve70,
      PCurve90,
    ] as PlanPredictCurvesResponsePayload<EmissionCurvesType>[];

    return result;
  });
};

export const parseAggregationName = (wellParameterName: string): string =>
  wellParameterName.split('_').slice(0, -1).join('_');

export const getVisibleCurvesLines = (curveLines: string[], curvesSelectedValues: string[]) => {
  const selectedValuesSet = new Set(curvesSelectedValues);

  return curveLines.filter((item) => Array.from(selectedValuesSet).some((value) => item.includes(value)));
};
